import React from 'react';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import Cookie from 'js-cookie';
import NextSeo from 'next-seo';
import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import { useTranslations } from '@lang';
import { fetchBasketDetails } from '@store/basket/actions';
import { getSettings } from '@store/setting/actions';
import BasketContainer from '@features/Basket/containers/Basket';
import { NormalizedStore } from '@store/entity/entity.types';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Routes } from '@common/constants';

type BasketProps = {
  language: string;
  propertyId: number;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const Basket: NextPage<BasketProps> = ({
  language,
  propertyId,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const isCondensedCheckout = useFeatureIsOn('feature.condensed-checkout-v1');
  const checkoutRoute = isCondensedCheckout ? Routes.CHECKOUT : Routes.CHECKOUT_SIGNIN;
  const pageName = t('app.basket');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <BasketContainer language={language} propertyId={propertyId} checkoutRoute={checkoutRoute} />
    </>
  );
};

type BasketInitialProps = {
  property: Property;
  store: NormalizedStore;
  language: string;
  url: string;
} & NextPageContext;

Basket.getInitialProps = async ({ store, property, language, url }: BasketInitialProps) => {
  const basketId = Cookie.get('basketId') as unknown as number;
  await Promise.all([
    ...(basketId ? [fetchBasketDetails(property.id, basketId, language)(store.dispatch)] : []),
    getSettings()(store.dispatch),
  ]);
  return {
    url,
    language,
    propertyId: property.id,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default Basket;
